import { useEffect, useRef, useState } from 'react';
import { useOnline } from '../lib/offline';
import { useQueue } from '../lib/queue';
import { Check, RefreshCw, WifiOff } from './icons';

/**
 * Slim fixed banner under the header: shows when offline, when items are
 * waiting to sync, and when a sync pass just completed. Click opens SyncSheet.
 */
export function OfflineBanner({ onOpenSync }: { onOpenSync: () => void }) {
  const online = useOnline();
  const { pending, failed, syncing } = useQueue();
  const [showSynced, setShowSynced] = useState(false);

  /* Only celebrate a sync or reconnect that actually happened - never on a fresh page load. */
  const hadWork = useRef(false);
  useEffect(() => {
    if (!online || syncing || pending > 0 || failed > 0) hadWork.current = true;
  }, [online, syncing, pending, failed]);

  useEffect(() => {
    if (!syncing && pending === 0 && failed === 0 && online) {
      if (hadWork.current) {
        hadWork.current = false;
        setShowSynced(true);
        const t = window.setTimeout(() => setShowSynced(false), 4000);
        return () => window.clearTimeout(t);
      }
      setShowSynced(false);
      return undefined;
    }
    setShowSynced(false);
    return undefined;
  }, [syncing, pending, failed, online]);

  if (!online) {
    return (
      <button type="button" className="banner banner--offline" onClick={onOpenSync} data-testid="offline-banner">
        <WifiOff size={14} />
        <span>Offline — changes are saved on this device</span>
      </button>
    );
  }

  if (failed > 0) {
    return (
      <button type="button" className="banner banner--failed" onClick={onOpenSync} data-testid="sync-banner">
        <RefreshCw size={14} />
        <span>
          {failed} item{failed === 1 ? '' : 's'} need{failed === 1 ? 's' : ''} attention
        </span>
      </button>
    );
  }

  if (pending > 0 || syncing) {
    return (
      <button type="button" className="banner banner--pending" onClick={onOpenSync} data-testid="sync-banner">
        <RefreshCw size={14} className={syncing ? 'spin' : ''} />
          <span>{syncing ? 'Syncing…' : `${pending} change${pending === 1 ? '' : 's'} waiting to sync`}</span>
      </button>
    );
  }

  if (showSynced) {
    return (
      <div className="banner banner--synced" role="status" data-testid="sync-done">
        <Check size={14} />
        <span>Back online — everything synced</span>
      </div>
    );
  }

  return null;
}
